import * as React from "react";
import { Link } from "react-router";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { Warning20Regular } from "@fluentui/react-icons";
import { searchContactsForDuplicateCheck } from "../../services/contactDuplicateCheckService";
import {
  rankCandidates,
  type DuplicateCheckInput,
  type RankedCandidate,
} from "../../utils/contactMatchKey";

/**
 * Live duplicate-check used by the contact-creation forms. After enough of the
 * name is typed (debounced), it searches existing contacts and surfaces likely
 * duplicates as a NON-blocking warning panel — creation always stays possible.
 */
export function useDuplicateCheck(
  input: DuplicateCheckInput & {
    enabled?: boolean;
    /**
     * C7 (Cluster ZUWEISEN) — verlangt der Trigger bei Personen zusätzlich einen
     * Vornamen? Default `true`: exakt das bisherige Verhalten, alle bestehenden
     * Masken bleiben unverändert.
     *
     * Die Mail-Schnellanlage setzt `false`. Aus einer Mail kennt man oft nur den
     * Nachnamen; mit dem Vornamen-Gate wäre die Dublettenwarnung dort nominell
     * erfüllt und praktisch wirkungslos — genau an der Stelle, an der die Gefahr
     * am größten ist. `rankCandidates` kommt ohne Vornamen zurecht: das
     * Namens-Gate greift auf `lastname`, der Vorname erhöht nur den Score.
     */
    requireFirstName?: boolean;
  },
): { results: RankedCandidate[]; loading: boolean } {
  const {
    isCompany,
    firstName,
    lastName,
    birthDate,
    email,
    enabled = true,
    requireFirstName = true,
  } = input;
  const [results, setResults] = React.useState<RankedCandidate[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const last = lastName.trim();
    const first = firstName.trim();
    // Trigger gate: Firma needs a name; Person needs last + at least 1 first char.
    // `enabled: false` schaltet die Suche ganz ab — beim Umwandeln eines
    // bestehenden Kontakts ist die Maske mit dessen Namen vorausgefüllt, die
    // Suche fände also den Kontakt selbst und meldete ihn als Doppelgänger.
    const ready =
      enabled &&
      (isCompany
        ? last.length >= 2
        : last.length >= 2 && (!requireFirstName || first.length >= 1));
    if (!ready) {
      setResults([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const candidates = await searchContactsForDuplicateCheck(last, controller.signal);
        const ranked = rankCandidates(
          { isCompany, firstName, lastName, birthDate, email },
          candidates,
        );
        setResults(ranked);
      } catch (e: any) {
        if (e?.name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [isCompany, firstName, lastName, birthDate, email, enabled, requireFirstName]);

  return { results, loading };
}

const useStyles = makeStyles({
  panel: {
    backgroundColor: "#fdf3d6",
    ...shorthands.border("1px", "solid", "#e8cf7a"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("10px", "14px"),
    color: "#8a6d00",
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    fontSize: "0.85rem",
  },
  head: { display: "flex", alignItems: "center", columnGap: "8px", fontWeight: 600 },
  hint: { fontSize: "0.78rem", opacity: 0.85 },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "10px",
    ...shorthands.padding("3px", "0"),
  },
  meta: { color: "#9a7e1f", fontSize: "0.76rem" },
  link: { color: "#6b4f00", fontWeight: 600 },
});

export const DuplicateWarningPanel: React.FC<{
  results: RankedCandidate[];
  loading?: boolean;
}> = ({ results, loading }) => {
  const s = useStyles();
  if (loading && results.length === 0) {
    return <div className={s.hint}>Prüfe auf Doppelgänger…</div>;
  }
  if (results.length === 0) return null;

  return (
    <div className={s.panel}>
      <div className={s.head}>
        <Warning20Regular />
        Mögliche Doppelgänger gefunden — bitte prüfen, ob der Kontakt bereits existiert:
      </div>
      <div className={s.hint}>
        Die Anlage ist trotzdem möglich. Hinweis: nur in deinem Sichtbereich geprüft.
      </div>
      {results.map(({ candidate: c }) => {
        const name =
          c.displayName ||
          [c.lastname, c.firstName].filter(Boolean).join(", ") ||
          `#${c.id}`;
        const meta = [
          c.contactNbrInt ? `Nr. ${c.contactNbrInt}` : "",
          c.birthDate ? String(c.birthDate).slice(0, 10) : "",
          c.city ?? "",
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <div key={c.id} className={s.row}>
            <span>
              {name}
              {meta ? <span className={s.meta}> — {meta}</span> : null}
            </span>
            <Link
              className={s.link}
              to={`/contacts/${c.id}`}
              target="_blank"
              rel="noreferrer"
            >
              öffnen
            </Link>
          </div>
        );
      })}
    </div>
  );
};
